import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImCheckmark, ImCross } from "react-icons/im";
import { useNavigate } from "react-router-dom";
import TademarkContext from "../../../context/trademark/context";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { ITrademark } from "../../../models/trademark/response";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Space from "../../general/antd/space";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import ViewBtn from "../../general/table-components/actions/view-btn";
import Table from "../../general/table-components/table";
import Image from "../../general/antd/image";
import styles from './styles.module.scss';
import Can from "../../../utils/rbac/can";
import { Permissions } from "../../../utils/Permissions/Permissions";

interface IProps { }

const TademarkTable: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(TademarkContext);

  const columns: ColumnProps<ITrademark>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 100,
      align: "center",
    },
    {
      title: t("trademark_name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      sorter: {
        multiple: 2,
      },
    },
    {
      title: t("order"),
      dataIndex: "order",
      align: "center",
      key: "order",
    },
    {
      title: t("new"),
      dataIndex: "isNew",

      align: "center",
      key: "isNew",
      render: (_, record) => {
        return (
          <>
            {record?.isNew ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },
    {
      title: t("export_purchase_invoice_report"),
      dataIndex: "exportPurchaseInvoiceReport",

      align: "center",
      key: "exportPurchaseInvoiceReport",
      render: (_, record) => {
        return (
          <>
            {record?.exportPurchaseInvoiceReport ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },
    {
      key: "image",
      title: t("image"),
      dataIndex: "brandImage",
      align: "center",
      render: (_, record) => {
        return record?.image ? (
          <Image src={record.image?.url} height={"25px"} preview={true} />
        ) : (
          "-"
        );
      },
    },
    {
      key: "logo",
      title: t("logo"),
      dataIndex: "brandLogo",
      align: "center",
      render: (_, record) => {
        return record?.logo ? (
          <Image src={record.logo?.url} height={"25px"} preview={true} />
        ) : (
          "-"
        );
      },
    },
    {
      title: t("actions"),
      width: 250,
      dataIndex: "",
      align: "center",
      key: "x",
      render: (_: any, record) => (
        <Space>
          <Can perform={Permissions.CAN_VIEW_TRADEMARK}>
            <ViewBtn
              onClick={(e) => {
                const url = `/trademarks/details/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
          </Can>
          <Can perform={Permissions.CAN_UPDATE_TRADEMARK}>
            <EditBtn
              onClick={(e) => {
                const url = `/trademarks/update/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
          </Can>
          <Can perform={Permissions.CAN_DELETE_TRADEMARK}>
            <DeleteBtn
              onConfirm={async () => {
                await actions.deleteTrademark(record?.id);
                eventManager.emit(EVENT_SUCCESS);
              }}
              loading={loading.includes("delete")}
            />
          </Can>
        </Space>
      ),
    },
  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        className={styles.table}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters
          );
        }}
      />
    </>
  );
};

export default TademarkTable;
